import { useCallback, useEffect, useState } from 'react';
import { api, type Company, type FxRevaluation } from '../api';
import type { Dictionary, Locale } from '../i18n';
import { amount, date as formatDate, money } from '../format';
import * as Icon from './Icons';
import { ErrorAlert, failureOf, type Failure } from './Alerts';
import type { Panel } from './Shell';

/** Domyślny dzień wyceny: koniec poprzedniego miesiąca — wycenia się po jego zamknięciu. */
function endOfPreviousMonth(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), 0)).toISOString().slice(0, 10);
}

/**
 * Wycena walut na dzień bilansowy (E7-S5, art. 30 ust. 1 uor).
 *
 * Ekran ma pokazać **różnicę przed jej zaksięgowaniem**: zapis w dzienniku jest nieodwracalny, więc
 * najpierw „Przelicz" (serwer liczy, ale nic nie zapisuje), a dopiero potem „Zaksięguj wycenę".
 * Kursów nie podpowiadamy — wpisuje je człowiek, bo to on odpowiada za to, że są to kursy średnie
 * NBP z dnia wyceny (kursów nie pobieramy, decyzja 29).
 */
export function FxRevaluationPanel({
  company,
  t,
  locale,
  editable,
  onGo,
  onPosted,
}: {
  company: Company;
  t: Dictionary;
  locale: Locale;
  editable: boolean;
  onGo?: (panel: Panel) => void;
  onPosted?: () => void;
}) {
  const [asOf, setAsOf] = useState(endOfPreviousMonth);
  const [report, setReport] = useState<FxRevaluation | null>(null);
  const [rates, setRates] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Failure | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setMessage(null);
    try {
      setReport(await api.fxRevaluation(company.id, asOf));
    } catch (failure) {
      setReport(null);
      setError(failureOf(failure));
    }
  }, [company.id, asOf]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  /* Waluty, o które trzeba zapytać: te, które w ogóle są na pozycjach do wyceny */
  const currencies = [...new Set((report?.items ?? []).map((item) => item.currency))].sort();
  const ready = currencies.length > 0 && currencies.every((code) => (rates[code] ?? '').trim() !== '');

  return (
    <div className="panel">
      <header>
        <h4>{t.journal.fx.title}</h4>
        <div className="sp">
          <input
            className="inp sm"
            type="date"
            aria-label={t.journal.fx.asOf}
            value={asOf}
            onChange={(event) => setAsOf(event.target.value)}
          />
        </div>
      </header>
      <p className="sm muted">{t.journal.fx.lead}</p>

      <ErrorAlert error={error} t={t} onGo={onGo} />
      {message && (
        <div className="alert a-ok">
          <Icon.Check />
          <div>
            <b>{message}</b>
          </div>
        </div>
      )}
      {/* Wycena na ten dzień już jest: druga policzyłaby różnicę od wycenionych sald */}
      {report?.entryNo != null && !message && (
        <div className="alert a-warn">
          <Icon.Warn />
          <div>
            <b>
              {t.journal.fx.exists} {report.entryNo}
            </b>
          </div>
        </div>
      )}

      {report?.items.length === 0 ? (
        <div className="empty">
          <Icon.Balance />
          <b>{t.journal.fx.none}</b>
          <p>{t.journal.fx.noneHint}</p>
        </div>
      ) : (
        report && (
          <>
            <div className="fields">
              {currencies.map((code) => (
                <div className="field" key={code}>
                  <label htmlFor={`fx-${code}`}>
                    {t.journal.fx.rate} {code}
                  </label>
                  <input
                    id={`fx-${code}`}
                    className="inp n"
                    inputMode="decimal"
                    value={rates[code] ?? ''}
                    onChange={(event) => setRates({ ...rates, [code]: event.target.value })}
                  />
                </div>
              ))}
            </div>

            <div className="tbl">
              <table>
                <thead>
                  <tr>
                    <th>{t.journal.fx.item}</th>
                    <th>{t.journal.fx.account}</th>
                    <th className="n">{t.journal.fx.balanceCurrency}</th>
                    <th className="n">{t.journal.fx.balance}</th>
                    <th className="n">{t.journal.fx.revalued}</th>
                    <th className="n">{t.journal.fx.difference}</th>
                  </tr>
                </thead>
                <tbody>
                  {report.items.map((item) => (
                    <tr key={`${item.kind}-${item.account}-${item.label}`}>
                      <td>
                        {item.label}
                        <span className="sm muted"> · {t.journal.fx.kinds[item.kind]}</span>
                      </td>
                      <td className="mono">{item.account}</td>
                      <td className="n">
                        {amount(item.balanceCurrency, locale)} {item.currency}
                      </td>
                      <td className="n">{amount(item.balance, locale)}</td>
                      <td className="n">{item.revalued ? amount(item.revalued, locale) : '—'}</td>
                      <td className="n">{item.difference ? amount(item.difference, locale) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={4}>{formatDate(report.asOf, locale)}</td>
                    <td className="n">{t.journal.fx.gain}</td>
                    <td className="n">{money(report.gain, locale)}</td>
                  </tr>
                  <tr>
                    <td colSpan={4} />
                    <td className="n">{t.journal.fx.loss}</td>
                    <td className="n">{money(report.loss, locale)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Rachunkowo różnica jest już w księgach; podatkowo bywa inaczej i trzeba to powiedzieć */}
            <p className="note">{t.journal.fx.taxNote}</p>

            {editable && (
              <div className="row">
                <button
                  type="button"
                  className="btn"
                  disabled={busy || !ready}
                  onClick={() =>
                    void run(async () => {
                      setReport(await api.revalueCurrencies(company.id, asOf, rates, true));
                      setMessage(null);
                    })
                  }
                >
                  {t.journal.fx.preview}
                </button>
                <button
                  type="button"
                  className="btn pri"
                  disabled={busy || !ready}
                  onClick={() =>
                    void run(async () => {
                      const posted = await api.revalueCurrencies(company.id, asOf, rates, false);
                      setReport(posted);
                      setMessage(
                        posted.entryNo == null
                          ? t.journal.fx.nothingToPost
                          : `${t.journal.fx.posted} ${posted.entryNo}`,
                      );
                      onPosted?.();
                    })
                  }
                >
                  {t.journal.fx.post}
                </button>
              </div>
            )}
          </>
        )
      )}
    </div>
  );
}
